import { createAuthClient } from "better-auth/react";
import { emailOTPClient } from "better-auth/client/plugins"

// Create Better Auth client
export const authClient = createAuthClient({
  baseURL: window.location.origin,
  plugins: [emailOTPClient()],
});

// Re-export types from Better Auth
export type { User, Session } from "better-auth/types";

// Legacy types for backward compatibility
export type AuthMethod = 'otp' | 'link';

export interface RequestEmailBody {
  email: string;
  method: AuthMethod;
}

// Error code constants for easy reference
export const AUTH_ERROR_CODES = {
  INVALID_EMAIL: 'invalid_email',
  RATE_LIMITED: 'rate_limited',
  SERVER_ERROR: 'server_error',
  INVALID_TOKEN: 'invalid_token',
  TOKEN_EXPIRED: 'token_expired',
  ALREADY_USED: 'already_used',
  NOT_IMPLEMENTED: 'not_implemented',
} as const;

// Helper functions for common auth operations
export const authHelpers = {
  /**
   * Check if error is due to rate limiting
   */
  isRateLimited: (error: unknown) => {
    return error instanceof Error && error.message.includes('rate limit');
  },
  
  /**
   * Check if error is due to invalid/expired token
   */
  isInvalidToken: (error: unknown) => {
    return error instanceof Error && (
      error.message.includes('invalid') ||
      error.message.includes('expired') ||
      error.message.includes('used')
    );
  },
  
  /**
   * Check if error is a server error (should retry)
   */
  isServerError: (error: unknown) => {
    return error instanceof Error && error.message.includes('server');
  },
  
  /**
   * Get user-friendly error message
   */
  getErrorMessage: (error: unknown): string => {
    if (!(error instanceof Error)) {
      return 'An unexpected error occurred';
    }
    
    if (error.message.includes('email')) {
      return 'Please enter a valid email address';
    }
    if (error.message.includes('rate limit')) {
      return 'Too many attempts. Please wait before trying again';
    }
    if (error.message.includes('expired')) {
      return 'Verification code has expired. Please request a new one';
    }
    if (error.message.includes('used')) {
      return 'This verification code has already been used';
    }
    if (error.message.includes('invalid')) {
      return 'Invalid verification code';
    }
    if (error.message.includes('server')) {
      return 'Server error. Please try again';
    }
    
    return error.message || 'An error occurred';
  }
};